import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import ThemeToggle from "./ThemeToggle";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Practice", href: "#practice" },
  { label: "Approach", href: "#approach" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#f7f3ea]/90 border-b border-[#d9cdb4]/60 shadow-lg shadow-[#0f1d3a]/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNavClick("#home"); }}
            className="flex items-center gap-3"
            data-testid="navbar-logo"
          >
            <img
              src="./images/logo-emblem.png"
              alt="Corpus Juris emblem"
              className={`h-10 lg:h-11 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            />
            <div className="flex flex-col leading-none">
              <span className={`font-heading text-lg lg:text-xl font-bold tracking-tight transition-colors ${scrolled ? "text-[#0f1d3a]" : "text-white"}`}>
                Corpus Juris
              </span>
              <span className={`hidden sm:block text-[10px] tracking-[0.25em] uppercase mt-0.5 transition-colors ${scrolled ? "text-[#b89968]" : "text-[#b89968]/80"}`}>
                Law Chambers
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className={`text-sm font-medium transition-colors hover:opacity-80 ${
                  scrolled ? "text-[#3b4a6b] hover:text-[#0f1d3a]" : "text-[#e0d5c0] hover:text-white"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </a>
            ))}
            <ThemeToggle variant="nav" scrolled={scrolled} />
            <a href="tel:9633717034" data-testid="nav-consult-btn">
              <Button className="glossy-btn rounded-full bg-[#b89968] text-[#0f1d3a] hover:bg-[#e0c29a] px-5 py-2 text-sm font-bold flex items-center gap-2 shadow-lg shadow-[#b89968]/30 tracking-wide">
                <Phone className="w-3.5 h-3.5" />
                Consult Now
              </Button>
            </a>
          </div>

          {/* Mobile: theme toggle + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle variant="nav" scrolled={scrolled} />
            <button
              data-testid="mobile-menu-toggle"
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <X className={`w-6 h-6 ${scrolled ? "text-[#0f1d3a]" : "text-white"}`} />
              ) : (
                <Menu className={`w-6 h-6 ${scrolled ? "text-[#0f1d3a]" : "text-white"}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="lg:hidden bg-[#f7f3ea] border-t border-[#d9cdb4] shadow-lg animate-fade-in">
            <div className="py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="block px-4 py-3 text-sm font-medium text-[#3b4a6b] hover:bg-[#ece4d6] hover:text-[#0f1d3a]"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {link.label}
                </a>
              ))}
              <div className="px-4 pt-1 pb-1">
                <ThemeToggle variant="mobile" />
              </div>
              <div className="px-4 pt-2">
                <a href="tel:9633717034" data-testid="mobile-consult-btn">
                  <Button className="glossy-btn w-full rounded-full bg-[#b89968] text-[#0f1d3a] hover:bg-[#e0c29a] font-bold tracking-wide">
                    <Phone className="w-4 h-4 mr-2" />
                    Consult Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
